import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  Divider,
  Heading,
  useDialogContainer,
  TextField,
  Flex,
  ComboBox,
  Text,
  Item,
  ToggleButton,
  useAsyncList,
} from "@adobe/react-spectrum";
import React, { useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import {
  error as ErrorToast,
  success as SuccessToast,
} from "@react/react-spectrum/Toast";
import PropTypes from "prop-types";
import { gtmhApi } from "../../../../api/gtmhApi";
import useUserProfile from "../../../../context/user-context";

export default function DemoteParentDialog({
  rowInfo,
  status,
  refreshTable,
  isPageLoading,
  setPageLoading,
}) {
  const { user } = useUserProfile();
  const { authState } = useOktaAuth();
  const dialog = useDialogContainer();

  const [isIdSearch, setIsIdSearch] = useState(false);
  const [isNameSearch, setIsNameSearch] = useState(false);
  const [demoteRequest, setDemoteRequest] = useState({
    parent_id: rowInfo.sub_id,
    new_parent_id: "",
    comment: "",
    user_id: user?.userId,
    user_name: user?.name,
    status,
  });
  const [errorMessage, setErrorMessage] = useState("");

  const targetAccountList = useAsyncList({
    async load({ filterText }) {
      if (!filterText)
        return {
          items: [],
        };
      const json = await gtmhApi.searchParents(
        {
          search_string: isIdSearch || isNameSearch ? "" : filterText,
          sub_id: !isIdSearch ? "" : filterText,
          sub_name: !isNameSearch ? "" : filterText,
          status: status === "active" ? ["active"] : ["active", "in-review"],
          parent_only: true,
        },
        authState.accessToken
      );
      const results = json?.results[0].results || [];
      return {
        items: results,
      };
    },
  });

  const handleDemote = () => {
    setPageLoading(true);
    gtmhApi
      .demoteSub(demoteRequest, authState.accessToken)
      .then((data) => {
        if (data.results?.results) {
          dialog.dismiss();
          refreshTable();
          setPageLoading(false);
          SuccessToast(`Successfully demoted parent ${rowInfo.sub_id}!`, {
            timeout: 5000,
          });
        } else {
          setPageLoading(false);
          setErrorMessage(data.results?.error_payload.message);
        }
      })
      .catch((error) => {
        console.log("error");
        dialog.dismiss();
        setPageLoading(false);
        ErrorToast(error.response?.data?.message || "Error demoting parent", {
          timeout: 5000,
        });
      });
  };

  const handleCancel = () => {
    dialog.dismiss();
  };

  return (
    <Dialog>
      <Heading>{`Demote Parent: ${rowInfo.parent_name}`}</Heading>
      <Divider />
      <Content>
        {errorMessage !== "" && (
          <Text UNSAFE_style={{ color: "red" }}>{errorMessage}</Text>
        )}
        <Flex marginBottom="15px">
          Promoting this sub will results in a new parent being create with the
          same attributes. Proceed to promote?
        </Flex>
        <ComboBox
          width="100%"
          label={
            <Flex height="32px" alignItems="center" gap="156px">
              <Flex width="200px">
                <Text UNSAFE_style={{ fontSize: "14px" }}>Parent</Text>
              </Flex>
              <Flex direction="column" alignItems="end" width="200px">
                <Flex height="32px" alignItems="center" gap="5px">
                  <ToggleButton
                    isSelected={isNameSearch}
                    onChange={setIsNameSearch}
                    isDisabled={isIdSearch}
                    height="24px"
                  >
                    Name Search
                  </ToggleButton>
                  <ToggleButton
                    isSelected={isIdSearch}
                    onChange={setIsIdSearch}
                    isDisabled={isNameSearch}
                    height="24px"
                  >
                    ID Search
                  </ToggleButton>
                </Flex>
              </Flex>
            </Flex>
          }
          // labelAlign="start"
          // isRequired
          inputValue={targetAccountList.filterText}
          selectedKey={demoteRequest.new_parent_id || null}
          onInputChange={(val) => {
            targetAccountList.setFilterText(val);
            // If user starts typing, clear selected parent
            setDemoteRequest({ ...demoteRequest, new_parent_id: "" });
          }}
          onSelectionChange={(val) => {
            const selected = targetAccountList.items.find(
              (item) => item.sub_id === val
            );
            if (selected) {
              setDemoteRequest({
                ...demoteRequest,
                new_parent_id: val.substring(0, val.length - 3),
              });
              targetAccountList.setFilterText(selected.sub_name); // show display name
            }
          }}
          items={targetAccountList.items}
          loadingState={targetAccountList.loadingState}
        >
          {(item) =>
            item && (
              <Item key={item.sub_id}>
                <Text>{item.sub_name}</Text>
              </Item>
            )
          }
        </ComboBox>
        <TextField
          label="Comment"
          width="100%"
          onChange={(val) =>
            setDemoteRequest({ ...demoteRequest, comment: val })
          }
          marginTop="10px"
        />
      </Content>
      <ButtonGroup>
        <Button onPress={handleCancel} variant="secondary">
          Cancel
        </Button>
        <Button
          onPress={handleDemote}
          color="primary"
          variant="accent"
          isDisabled={isPageLoading}
        >
          Demote
        </Button>
      </ButtonGroup>
    </Dialog>
  );
}

DemoteParentDialog.propTypes = {
  rowInfo: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.object),
    ])
  ).isRequired,
  status: PropTypes.string.isRequired,
  refreshTable: PropTypes.func.isRequired,
  setPageLoading: PropTypes.func.isRequired,
  isPageLoading: PropTypes.bool.isRequired,
};
